import {
  EXTERNAL_EMAIL_CAPTURE_QUERY_KEY,
  SENT_DRAFT_CITATIONS_QUERY_KEY,
} from '@/hooks/pii-query-key-roots';

import type { Query } from '@tanstack/react-query';

/**
 * Single source of truth for the query-key prefixes whose cached data is
 * "share-gated": a `correspondence_shares` or `task_shares` row is what confers
 * a recipient access to the constituent PII behind them — email subjects,
 * snippets and draft bodies; task titles, descriptions and subtask titles;
 * comment bodies; attachment filenames; linked-thread senders.
 *
 * The invariant this registry holds: a share-gated key is BOTH
 *   (a) hard-evicted (`removeQueries`) when the share is revoked, so the open
 *       surface blanks without a refocus — the app-root ShareAccessRealtime
 *       provider (WP-2), AND
 *   (b) excluded from localStorage persistence, so a revoked recipient who
 *       reloads cannot re-read it from a stale snapshot. localStorage survives
 *       logout and a revoke for up to the persist `maxAge` (24h) — the PUB-894
 *       class of leak.
 * Either both, or neither. The two lists were hand-maintained separately and
 * drifted: the correspondence detail/timeline/draft-history/interactions
 * surfaces were evicted yet still persisted. Routing both consumers
 * (`shouldDehydrateQuery` and the eviction handlers) through this one list
 * structurally prevents that recurrence.
 *
 * Only HARD-evicted surfaces belong here. List surfaces that are INVALIDATED
 * (soft refetch) rather than removed — filter counts, saved-view counts,
 * assignments — rely on RLS re-deciding their visibility on the
 * post-revoke refetch, so they are NOT share-gated in this sense. The inbox
 * index is likewise not share-gated, but its persistence is separately gated
 * by `PERSIST_EMAIL_INDEX` in query-persistence.ts (shipped off).
 */

/** A leading-segment prefix that identifies a share-gated query key. */
export type QueryKeyPrefix = readonly [string, ...string[]];

// Correspondence-domain share-gated prefixes, hard-evicted by
// `removeCorrespondenceAccessCaches` on a `correspondence_shares` revoke, split
// by how the cache key relates to the correspondenceId (the eviction handler
// needs the distinction; persistence consumes only the union below).

/**
 * Surfaces keyed by the correspondenceId (`['correspondence', <surface>, orgId,
 * corrId]`, plus the corrId-scoped `comments` / `email-tasks` children) - evicted
 * scoped to that id.
 */
const CORRESPONDENCE_CORR_KEYED_PREFIXES = [
  ['correspondence', 'detail'],
  ['correspondence', 'timeline'],
  ['correspondence', 'draft-history'],
  ['correspondence', 'interactions'],
  ['correspondence', 'shares'],
  ['correspondence', 'capabilities'],
  ['comments'],
  ['email-tasks'],
] as const satisfies readonly QueryKeyPrefix[];

/**
 * Correspondence-adjacent surfaces the corrId-scoped eviction does not reach,
 * evicted by whole namespace. `email-drafts-thread` is keyed by threadId,
 * `task-linked-correspondence` by taskId, `pasted-email-earlier-messages` by
 * interactionId, `pasted-email-senders` (sender names and addresses) by viewer
 * then corrId, `external-email-capture` (move targets, suggestions) by the
 * case the picker was opened from, `correspondence-classification` (a pasted
 * case's category, priority and sentiment) by orgId then corrId,
 * `sent-draft-citations` (a sent email's draft body and citations) by viewer,
 * office, then the sent email's metadata id;
 * `email-drafts-correspondence` (thread-less drafts and
 * marked-sent replies) carries the corrId, but at index 1 rather than where
 * the corr-keyed surfaces put it. Every one reads again once evicted (RLS
 * re-gates) and revokes are rare, so evicting the whole namespace is safe.
 */
export const CORRESPONDENCE_NAMESPACE_EVICT_PREFIXES = [
  ['email-drafts-thread'],
  ['email-drafts-correspondence'],
  ['pasted-email-earlier-messages'],
  ['pasted-email-senders'],
  ['task-linked-correspondence'],
  [EXTERNAL_EMAIL_CAPTURE_QUERY_KEY],
  ['correspondence-classification'],
  [SENT_DRAFT_CITATIONS_QUERY_KEY],
] as const satisfies readonly QueryKeyPrefix[];

/** The union - what persistence (`isShareGatedQueryKey`) consumes. */
export const CORRESPONDENCE_SHARE_GATED_PREFIXES = [
  ...CORRESPONDENCE_CORR_KEYED_PREFIXES,
  ...CORRESPONDENCE_NAMESPACE_EVICT_PREFIXES,
] as const satisfies readonly QueryKeyPrefix[];

/**
 * Task-domain share-gated prefixes: hard-evicted by `evictRevokedTaskShare` on
 * a `task_shares` revoke. A task share NEVER confers correspondence-content
 * access, so this covers only task surfaces and their PII-bearing children
 * (comment bodies, attachment filenames, linked-thread subjects, task
 * correspondence summaries).
 */
export const TASK_SHARE_GATED_PREFIXES = [
  ['tasks'],
  ['task'],
  ['subtasks'],
  ['my-tasks'],
  ['my-tasks-by-org'],
  ['comments'],
  ['task-attachments'],
  ['task-linked'],
  ['task-correspondence'],
] as const satisfies readonly QueryKeyPrefix[];

/**
 * The union of every share-gated prefix across both domains, deduped by the
 * joined prefix so `comments` (shared by task and correspondence child surfaces)
 * collapses to one entry. This is the list `shouldDehydrateQuery` consults.
 */
export const SHARE_GATED_QUERY_PREFIXES: readonly QueryKeyPrefix[] = [
  ...new Map(
    [...CORRESPONDENCE_SHARE_GATED_PREFIXES, ...TASK_SHARE_GATED_PREFIXES].map(
      (prefix) => [prefix.join(' '), prefix] as const,
    ),
  ).values(),
];

/** True iff `queryKey` starts with `prefix` segment-for-segment. */
export function queryKeyMatchesPrefix(
  queryKey: readonly unknown[],
  prefix: QueryKeyPrefix,
): boolean {
  if (queryKey.length < prefix.length) {
    return false;
  }
  for (const [index, segment] of prefix.entries()) {
    if (queryKey[index] !== segment) {
      return false;
    }
  }
  return true;
}

/** True iff `queryKey` starts with any prefix in `prefixes`. */
export function queryKeyMatchesAnyPrefix(
  queryKey: readonly unknown[],
  prefixes: readonly QueryKeyPrefix[],
): boolean {
  return prefixes.some((prefix) => queryKeyMatchesPrefix(queryKey, prefix));
}

/**
 * True iff the query's key is share-gated - its cached data is only visible via
 * an active share and MUST be both evicted on revoke and kept out of
 * localStorage. Used by `shouldDehydrateQuery` (persistence) and the
 * share-access eviction handlers (realtime), so the two can never drift.
 */
export function isShareGatedQueryKey(queryKey: readonly unknown[]): boolean {
  return queryKeyMatchesAnyPrefix(queryKey, SHARE_GATED_QUERY_PREFIXES);
}

/** Convenience overload for callers holding a `Query` (e.g. `shouldDehydrateQuery`). */
export function isShareGatedQuery(query: Pick<Query, 'queryKey'>): boolean {
  return isShareGatedQueryKey(query.queryKey);
}
